import { Link, LinkButton } from "~/common/components/Link";
import { ToggleIcon } from "~/common/components/icon/ToggleIcon";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { Card } from "~/features/configurator/Linear/components/Card";

import classes from "~/features/selectionAssistant/gearbox/components/CharacteristicsFilter/CharacteristicsFilter.module.less";
import { RadioFilter } from "~/features/selectionAssistant/gearbox/components/CharacteristicsFilter/components/RadioFilter";
import { SingleSelectFilter } from "~/features/selectionAssistant/gearbox/components/CharacteristicsFilter/components/SingleSelectFilter";
import { CheckboxFilter } from "~/features/selectionAssistant/gearbox/components/CharacteristicsFilter/components/CheckboxFilter";
import { MultiSelectFilter } from "~/features/selectionAssistant/gearbox/components/CharacteristicsFilter/components/MultiSelectFilter";
import { Button } from "~/common/mui";
import { IconType } from "~/common/components/icon/IconType";

import { type FacetFilterDefinition } from "~/features/selectionAssistant/gearbox/models";
import classNames from "classnames";
import {
  CHARACTERISTIC_WITH_FLANGE,
  Characteristics,
  FLANGE_SPECIFIC_CHARACTERISTICS,
  selectedValuesContainFlange,
} from "~/features/selectionAssistant/gearbox/components/CharacteristicsFilter/constants";
import { type CharacteristicKeys } from "~/features/selectionAssistant/gearbox/components/CharacteristicsFilter/types";
import { Trans, useTranslation } from "react-i18next";
import { splitBy } from "~/common/utils/reducers";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";

interface Props {
  className?: string;
  resultCount: Record<CharacteristicKeys, Record<string, number>>;
  onChange: (characteristic: CharacteristicKeys, value: string[]) => void;
  onReset: () => void;
  definitions: Record<CharacteristicKeys, FacetFilterDefinition>;
  values: Record<CharacteristicKeys, string[]>;
}

const FILTERS = {
  [Characteristics.PRODUCT_TYPE]: {
    Component: RadioFilter,
    className: classes.productType,
    noResultBehavior: "none",
  },
  [Characteristics.BUILD_DESIGN]: {
    Component: SingleSelectFilter,
    className: classes.buildDesign,
    noResultBehavior: "disable",
  },
  [Characteristics.BUILD_TYPE]: {
    Component: CheckboxFilter,
    className: classes.buildType,
    noResultBehavior: "disable",
  },
  [Characteristics.BUILD_SIZE]: {
    Component: MultiSelectFilter,
    className: classes.disable,
    noResultBehavior: "disable",
  },
  [Characteristics.GEAR_RATIO]: {
    Component: MultiSelectFilter,
    className: classes.gearRatio,
    noResultBehavior: "disable",
  },
  [Characteristics.ENGINE_CONSTRUCTION_VARIANT]: {
    Component: SingleSelectFilter,
    className: classes.engineVariant,
    noResultBehavior: "disable",
  },
  [Characteristics.INSTALLATION_POSITION]: {
    Component: SingleSelectFilter,
    className: classes.installationPosition,
    noResultBehavior: "disable",
  },
};

export const getInterchangeLink = () => "https://interchange.schaeffler.com/start.do?lang=en";

export const useCharacteristicFilterLogic = ({
  definitions,
  onChange,
  resultCount,
  values,
}: Pick<Props, "definitions" | "onChange" | "resultCount" | "values">) => {
  const showFlangeCharacteristics = selectedValuesContainFlange(values[CHARACTERISTIC_WITH_FLANGE]);

  const filterConfigs = (Object.keys(FILTERS) as CharacteristicKeys[])
    .map((code) => ({
      value: values[code] ?? [],
      onChange: (value: string[]) => onChange(code as CharacteristicKeys, value),
      definition: definitions[code],
      resultCount: resultCount[code],
      flangeOnly: FLANGE_SPECIFIC_CHARACTERISTICS.includes(code),
      ...FILTERS[code],
    }))
    // Sanity check
    .filter(({ definition }) => !!definition);

  const [buttonSelects, dropdownSelects] = splitBy(filterConfigs, (def) =>
    [RadioFilter, CheckboxFilter].includes(def.Component),
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderFilter = ({ Component, flangeOnly, className, ...rest }: any) => (
    <div
      key={rest.definition.code}
      className={classNames(className, classes.filterContainer, flangeOnly && !showFlangeCharacteristics && "hidden")}
    >
      <Component {...rest} />
    </div>
  );

  return {
    allFilters: filterConfigs,
    buttonFilters: buttonSelects,
    dropdownFilters: dropdownSelects,
    renderFilter,
    showFlangeCharacteristics,
  };
};

export const CharacteristicsFilter = ({ className, values, definitions, onChange, resultCount, onReset }: Props) => {
  const { val: open, toggle: toggleOpen } = useBooleanState(true);
  const { t } = useTranslation();

  const { buttonFilters, dropdownFilters, renderFilter, showFlangeCharacteristics } = useCharacteristicFilterLogic({
    definitions,
    onChange,
    resultCount,
    values,
  });

  return (
    <div className={className}>
      <div className={classes.titleSection}>
        <div className="d-flex flex-align-center">
          <h2 className={classes.title}>{t("page.sa.gearbox.characteristics.title")}</h2>
          <LinkButton iconBefore={<ToggleIcon isActive={open} />} onClick={toggleOpen}>
            {t(`page.sa.gearbox.${open ? "collapse" : "expand"}`)}
          </LinkButton>
        </div>

        <Button
          variant={"text"}
          startIcon={<DefaultButtonIcon type={IconType.Reset} />}
          onClick={onReset}
          className={classes.resetBtn}
          tracking-aux-section-marker={"reset"}
          tracking-aux-option-marker={"reset"}
        >
          {t(`page.sa.gearbox.btn.resetAll`)}
        </Button>
      </div>

      <Card
        disableHover={true}
        className={classNames(classes.card, showFlangeCharacteristics && classes.extraDropdowns)}
        hidden={!open}
      >
        <div className={classes.cardTitle}>
          <Trans i18nKey={"page.sa.gearbox.characteristics.description.desktop"}>
            Select characteristics or <Link href={getInterchangeLink()}>search via a competitor designation</Link> to
            find a suitable precision strain wave gear
          </Trans>
        </div>
        <div className={classes.tabletOnlyColumn}>{buttonFilters.map(renderFilter)}</div>
        <div className={classes.tabletOnlyColumn}>{dropdownFilters.map(renderFilter)}</div>
      </Card>
    </div>
  );
};
